import Link from 'next/link';
import ImageWithFallback from './ImageWithFallback';
import { useCallback, useRef, useState } from 'react';
import useFavorites from '@/hooks/useFavorites';
import { Check, Phone, Tag } from 'lucide-react';
import { useRouter } from 'next/navigation';
import anime from 'animejs';
import { getCategorySlugFromName } from '@/lib/category-slugs';

interface ProductCardProps {
  id: string;
  name: string;
  image?: string;
  category?: string;
  discountPercentage?: number;
  slug?: string;
  description?: string;
  features?: string[];
  images?: string[];
  price?: number;
  priority?: boolean;
  onDetailsClick?: (product: ProductCardProps) => void;
}

const ProductCard = ({ id, name, image, category, discountPercentage, slug, description, features, images: allImages, price, priority, onDetailsClick }: ProductCardProps) => {
  const { isFavorite, addFavorite, reloadFavorites } = useFavorites();
  const router = useRouter();
  const [quoteAdded, setQuoteAdded] = useState(false);
  const quoteAnimRef = useRef<HTMLButtonElement>(null);
  
  // ID'nin string olduğundan emin olalım
  const normalizedId = String(id);
  const isAlreadyInQuoteList = isFavorite(normalizedId) || quoteAdded;
  
  // Ürün verisini normalize etme
  const getNormalizedProduct = useCallback((): ProductCardProps => {
    return {
      id: normalizedId,
      name: name || 'İsimsiz Ürün',
      image: (image || allImages?.[0] || '/images/placeholder.webp') as string,
      category: category || 'Diğer',
      slug: slug || normalizedId,
      description: description || '',
      features: features || [],
      images: allImages || [],
      price: price
    };
  }, [normalizedId, name, image, category, slug, description, features, allImages, price]);
  
  // Toplu fiyat teklifi isteği
  const handleQuoteRequest = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Ürünü favorilere ekleyelim ve toplu teklif sayfasına yönlendirelim
    try {
      console.log(`[ProductCard] ${name} favorilere ekleniyor ve toplu teklif sayfasına yönlendiriliyor...`);
      
      // Ürün verisini normalize et
      const product = getNormalizedProduct();
      const favoriteProduct = {
        id: product.id,
        name: product.name,
        image: (product.image || '/images/placeholder.webp'),
        category: product.category || 'Diğer',
        slug: product.slug,
      };
      
      // Favorilere ekle (eğer zaten favorilerde değilse)
      if (!isFavorite(normalizedId)) {
        addFavorite(favoriteProduct);
        reloadFavorites();
        console.log(`[ProductCard] Ürün favorilere eklendi: ${name}`);
      }

      setQuoteAdded(true);
      if (quoteAnimRef.current) {
        anime({
          targets: quoteAnimRef.current,
          scale: [1, 1.04, 1],
          duration: 320,
          easing: 'easeOutQuad'
        });
      }
    } catch (error) {
      console.error(`[ProductCard] Teklif isteği sırasında hata:`, error);
    }
  }, [name, isFavorite, addFavorite, getNormalizedProduct, reloadFavorites, normalizedId]);

  const handleContactClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push('/iletisim');
  }, [router]);

  return (
    <div className="group relative overflow-visible rounded-lg border border-gray-100 bg-white shadow-lg transition-shadow duration-300 hover:shadow-xl">
      <div className="absolute right-3 top-3 z-10">
        <button
          type="button"
          onClick={handleContactClick}
          className="relative w-8 h-8 flex items-center justify-center rounded-full bg-white/85 backdrop-blur-sm text-gray-700 hover:text-primary hover:bg-red-50 transition-all"
          aria-label="İletişim sayfasına git"
        >
          <Phone size={17} />
        </button>
      </div>

      <Link
        href={`/urunler/${getCategorySlugFromName(category || '') || 'diger'}/${slug || id}`}
        className="relative block aspect-square w-full min-h-[208px] overflow-hidden rounded-t-lg bg-gray-100"
      >
        {image ? (
          <ImageWithFallback
            src={image}
            alt={`${category || 'Filiz Kozmetik'} - ${name?.trim() || 'ürün görseli'}`}
            width={300}
            height={300}
            priority={priority}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            fallbackSrc="/images/placeholder.webp"
            fallbackComponent={
              <div className="w-full h-full flex items-center justify-center bg-gray-100">
                {name ? (
                  <span className="text-gray-400">{name.substring(0, 1)}</span>
                ) : (
                  <span className="text-gray-400">?</span>
                )}
              </div>
            }
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            {name ? (
              <span className="text-gray-400">{name.substring(0, 1)}</span>
            ) : (
              <span className="text-gray-400">?</span>
            )}
          </div>
        )}
        
        {discountPercentage && discountPercentage > 0 && (
          <div className="absolute top-3 left-3 bg-primary text-white text-xs font-bold rounded-full px-2 py-1 z-10">
            İndirimli Ürün
          </div>
        )}
        
        {category && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
            <span className="text-white text-xs">{category}</span>
      </div>
        )}
      </Link>
      
      <div className="rounded-b-lg bg-white p-4">
        <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-primary transition-colors">
            {name}
          </h3>
        
        <div className="mt-3 flex flex-col gap-2">
          <button
            ref={quoteAnimRef}
            type="button"
            onClick={handleQuoteRequest}
            className={`inline-flex items-center justify-center w-full px-3 py-2 text-xs font-medium rounded-md transition-colors ${
              isAlreadyInQuoteList
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            {isAlreadyInQuoteList ? (
              <>
                <span className="mr-1 inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-600 text-white">
                  <Check className="h-3 w-3" />
                </span>
                Eklendi
              </>
            ) : (
              <>
                <Tag className="h-3 w-3 mr-1" />
                Fiyat teklif listesine ekle
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;